/**
 * Authoritative Agent health for Runtime, heartbeats, and support bundles.
 * Canalerts src/utils/agentHealthModel.js and the backend
 * services/agent_health_model.py must keep the same states and reason codes.
 *
 * online requires a current heartbeat and every required signal healthy.
 * A live process or a fresh heartbeat alone is not online.
 * Paging left unconfigured is not_configured and does not degrade the Agent.
 */

const STATES = ['dormant', 'starting', 'online', 'degraded', 'offline'];

function numberOr(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function heartbeatThresholdSeconds(expectedHeartbeatSeconds) {
  const expected = numberOr(expectedHeartbeatSeconds, 30);
  const bounded = expected > 0 ? expected : 30;
  return Math.max(bounded * 3, 90);
}

function parseTime(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function heartbeatAgeSeconds(heartbeatAt, now) {
  const hb = parseTime(heartbeatAt);
  if (hb == null) return null;
  return Math.max(0, Math.floor((now.getTime() - hb) / 1000));
}

function normalizePaging(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text || text === 'none' || text === 'not_configured' || text === 'unconfigured') {
    return 'not_configured';
  }
  return text;
}

function evaluateAgentHealth(input = {}, now = new Date()) {
  const source = input || {};
  const moment = now instanceof Date ? now : new Date(now);
  const disabled = source.enabled === false || source.disabled === true
    || String(source.status || '').toLowerCase() === 'disabled';
  const activated = source.activated === true || Boolean(String(source.agentId || '').trim());
  const age = heartbeatAgeSeconds(source.heartbeatAt || source.lastHeartbeatAt, moment);
  const threshold = heartbeatThresholdSeconds(source.expectedHeartbeatSeconds);
  const current = age != null && age <= threshold;
  const pagingAuthentication = normalizePaging(source.pagingAuthentication);
  const pagingListener = normalizePaging(source.pagingListener);

  if (disabled) {
    return { state: 'dormant', reasons: ['agent_disabled'], freshness: 'not_enabled', heartbeatAgeSeconds: age, pagingAuthentication, pagingListener };
  }
  if (!activated) {
    return { state: 'dormant', reasons: ['never_enabled'], freshness: 'not_enabled', heartbeatAgeSeconds: age, pagingAuthentication, pagingListener };
  }

  const startingAge = numberOr(source.startingAgeSeconds, 0);
  if (source.starting === true && startingAge >= 0 && startingAge < 120 && !current) {
    return { state: 'starting', reasons: ['startup_window'], freshness: 'starting', heartbeatAgeSeconds: age, pagingAuthentication, pagingListener };
  }

  if (!current) {
    return {
      state: 'offline',
      reasons: [age == null ? 'heartbeat_missing' : 'heartbeat_stale'],
      freshness: age == null ? 'never_seen' : 'last_known',
      heartbeatAgeSeconds: age,
      pagingAuthentication,
      pagingListener,
    };
  }

  const reasons = [];
  const runtimeProcess = String(source.runtimeProcess || '').toLowerCase();
  const nodeAgentProcess = String(source.nodeAgentProcess || '').toLowerCase();
  const cloud = String(source.cloud || '').toLowerCase();
  const mqtt = String(source.mqtt || '').toLowerCase();
  const authentication = String(source.authentication || '').toLowerCase();

  if (!runtimeProcess) reasons.push('diagnostics_incomplete');
  else if (runtimeProcess !== 'running') reasons.push('runtime_not_running');

  if (!nodeAgentProcess) reasons.push('diagnostics_incomplete');
  else if (nodeAgentProcess !== 'running') reasons.push('node_agent_not_running');

  if (!cloud) reasons.push('diagnostics_incomplete');
  else if (cloud !== 'reachable') reasons.push('cloud_unreachable');

  if (!mqtt) reasons.push('mqtt_unreported');
  else if (mqtt !== 'connected') reasons.push('mqtt_disconnected');

  if (!authentication) reasons.push('authentication_telemetry_unavailable');
  else if (authentication === 'revoked') reasons.push('authentication_revoked');
  else if (authentication === 'unknown') reasons.push('authentication_telemetry_unavailable');
  else if (authentication !== 'ok') reasons.push('authentication_failed');

  if (pagingListener === 'failed' || pagingListener === 'error') reasons.push('paging_listener_failed');
  else if (pagingAuthentication === 'configured' && pagingListener !== 'running' && pagingListener !== 'listening') {
    reasons.push('paging_listener_failed');
  }

  const unique = [...new Set(reasons)];
  if (unique.length) {
    return { state: 'degraded', reasons: unique, freshness: 'live', heartbeatAgeSeconds: age, pagingAuthentication, pagingListener };
  }
  return { state: 'online', reasons: [], freshness: 'live', heartbeatAgeSeconds: age, pagingAuthentication, pagingListener };
}

function healthColor(state) {
  if (state === 'online') return 'green';
  if (state === 'degraded' || state === 'starting') return 'amber';
  if (state === 'dormant') return 'grey';
  return 'red';
}

const DIAGNOSTIC_FIELDS = [
  'schemaVersion',
  'tenantId',
  'agentId',
  'agentType',
  'runtimeVersion',
  'nodeAgentVersion',
  'buildId',
  'osFamily',
  'osVersion',
  'uptimeSeconds',
  'memoryRssBytes',
  'runtimeProcess',
  'nodeAgentProcess',
  'cloud',
  'mqtt',
  'lastCloudAt',
  'lastMqttAt',
  'authentication',
  'pagingListener',
  'pagingAuthentication',
  'restartCount',
  'lastErrorCode',
  'effectiveHealth',
  'reasons',
  'timestamp',
];

function allowlistedDiagnostics(input = {}) {
  const out = { schemaVersion: 1 };
  DIAGNOSTIC_FIELDS.forEach((field) => {
    if (field === 'schemaVersion') return;
    if (input[field] !== undefined && input[field] !== null && input[field] !== '') {
      out[field] = input[field];
    }
  });
  return out;
}

function healthFromAgentDocument(agent = {}, now = new Date()) {
  const snapshot = agent.diagnostics && typeof agent.diagnostics === 'object' ? agent.diagnostics : {};
  const disabled = agent.deleted || agent.revokedAt || agent.decommissionedAt || agent.enabled === false || agent.disabled === true;
  const mqttConnected = agent.mqttConnected === true || String(agent.mqttConnected || '').toLowerCase() === 'true';
  return evaluateAgentHealth({
    agentId: agent.agentId || agent.id || snapshot.agentId,
    activated: Boolean(agent.agentId || agent.id || snapshot.agentId),
    enabled: disabled ? false : agent.enabled !== false,
    disabled,
    status: agent.status,
    expectedHeartbeatSeconds: agent.expectedHeartbeatSeconds || 30,
    heartbeatAt: agent.lastHeartbeatAt || agent.last_heartbeat || agent.lastHeartbeat || agent.lastSeen,
    runtimeProcess: snapshot.runtimeProcess,
    nodeAgentProcess: snapshot.nodeAgentProcess,
    cloud: snapshot.cloud,
    mqtt: snapshot.mqtt || (mqttConnected ? 'connected' : ''),
    authentication: snapshot.authentication,
    pagingListener: snapshot.pagingListener,
    pagingAuthentication: snapshot.pagingAuthentication,
  }, now);
}

export {
  STATES,
  DIAGNOSTIC_FIELDS,
  heartbeatThresholdSeconds,
  heartbeatAgeSeconds,
  evaluateAgentHealth,
  healthFromAgentDocument,
  healthColor,
  normalizePaging,
  allowlistedDiagnostics,
};
